import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { ChefHat, Check, Undo2, UtensilsCrossed } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const ago = (iso) => Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));

function Sala({ d, call }) {
  const done = (t, c) => d.calls.find((x) => x.tavolo === t && x.portata === c);
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="live-sala">
      {Object.entries(d.tables).map(([t, info]) => {
        const next = d.courses.find((c) => !done(t, c));
        return (
          <div key={t} className="rounded-2xl bg-white/10 p-4 space-y-2" data-testid={`live-table-${t}`}>
            <div className="flex justify-between items-baseline"><span className="font-serif-it text-2xl">{t}</span><span className="text-xs text-white/60">{info.ospiti} ospiti{info.bambini ? ` • ${info.bambini} bimbi` : ""}</span></div>
            {info.allergie.length > 0 && <div className="text-xs text-[#FCD34D]">⚠ {info.allergie.join(" • ")}</div>}
            <div className="flex flex-wrap gap-1">{d.courses.map((c) => { const x = done(t, c); return <span key={c} className={`text-[10px] rounded-full px-2 py-0.5 ${x?.stato === "uscito" ? "bg-[#34B3B0]" : x ? "bg-[#E07A5F]" : "bg-white/10 text-white/50"}`}>{c}</span>; })}</div>
            {next ? <button onClick={() => call(t, next)} className="w-full rounded-xl bg-[#34B3B0] hover:bg-[#2A9D8F] py-3 font-semibold transition-colors" data-testid={`live-call-${t}`}>Pronto per: {next}</button> : <div className="text-center text-sm text-white/60 py-3">Servizio completato ✓</div>}
          </div>
        );
      })}
    </div>
  );
}

function Cucina({ d, set }) {
  const open = d.calls.filter((x) => x.stato === "chiamato");
  const out = d.calls.filter((x) => x.stato === "uscito").slice(-8).reverse();
  return (
    <div className="space-y-4" data-testid="live-cucina">
      {!open.length && <div className="rounded-2xl bg-white/10 p-8 text-center text-white/70">Nessuna chiamata dalla sala.</div>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{open.map((x) => { const m = ago(x.at); const al = d.tables[x.tavolo]?.allergie || []; return (
        <div key={x.id} className={`rounded-2xl p-4 space-y-2 ${m >= 10 ? "bg-[#991B1B]" : m >= 5 ? "bg-[#B45309]" : "bg-white/10"}`} data-testid={`live-ticket-${x.tavolo}`}>
          <div className="flex justify-between"><span className="font-serif-it text-3xl">{x.tavolo}</span><span className="text-2xl font-mono">{m}′</span></div>
          <div className="text-lg font-semibold">{x.portata}</div>
          <div className="text-xs text-white/70">{d.tables[x.tavolo]?.ospiti || 0} coperti • chiamato da {x.by}</div>
          {al.length > 0 && <div className="text-xs text-[#FCD34D]">⚠ {al.join(" • ")}</div>}
          <button onClick={() => set(x.id, "uscito")} className="w-full rounded-xl bg-white text-[#0D2B3A] py-3 font-semibold" data-testid={`live-out-${x.tavolo}`}><Check size={16} className="inline mr-1" /> Uscito</button>
        </div>); })}</div>
      {out.length > 0 && <div className="text-sm text-white/60 space-y-1"><div className="uppercase text-[10px] tracking-widest">Ultime uscite</div>{out.map((x) => <div key={x.id} className="flex justify-between"><span>{x.tavolo} • {x.portata}</span><button onClick={() => set(x.id, "chiamato")} className="text-white/50 hover:text-white" data-testid={`live-undo-${x.id}`}><Undo2 size={13} /></button></div>)}</div>}
    </div>
  );
}

export default function LiveService() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [view, setView] = useState(localStorage.getItem("live-view") || "sala");
  const load = useCallback(() => api.get(`/contracts/${id}/live`).then((r) => setD(r.data)).catch(() => {}), [id]);
  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [load]);
  const call = (tavolo, portata) => api.post(`/contracts/${id}/live`, { tavolo, portata }).then(load).catch((e) => toast.error(errMsg(e)));
  const set = (cid, stato) => api.put(`/live/${cid}`, { stato }).then(load).catch((e) => toast.error(errMsg(e)));
  const pick = (v) => { setView(v); localStorage.setItem("live-view", v); };
  if (!d) return <div className="text-sm text-[#7B929C]">Caricamento…</div>;
  return (
    <div className="-mx-4 sm:-mx-6 lg:-mx-8 -my-6 min-h-screen bg-[#0D2B3A] text-white p-4 sm:p-6 space-y-4" data-testid="live-page">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div><div className="text-xs uppercase tracking-[0.25em] text-[#9FE3E0]">Servizio in corso • {d.number}</div><h1 className="font-serif-it text-3xl">{d.cliente_nome}</h1></div>
        <div className="flex bg-white/10 rounded-full p-1">{[["sala", UtensilsCrossed, "Sala"], ["cucina", ChefHat, "Cucina"]].map(([k, I, l]) => <button key={k} onClick={() => pick(k)} className={`px-5 py-2 rounded-full text-sm flex items-center gap-2 ${view === k ? "bg-white text-[#0D2B3A]" : ""}`} data-testid={`live-view-${k}`}><I size={15} /> {l}{k === "cucina" && d.calls.filter((x) => x.stato === "chiamato").length > 0 && <span className="bg-[#E07A5F] text-white rounded-full px-1.5 text-xs">{d.calls.filter((x) => x.stato === "chiamato").length}</span>}</button>)}</div>
      </div>
      {!Object.keys(d.tables).length && <div className="rounded-xl bg-white/10 p-4 text-sm">Nessun tavolo: assegna i tavoli in «Mappa sala» o «Ospiti e tavoli».</div>}
      {view === "sala" ? <Sala d={d} call={call} /> : <Cucina d={d} set={set} />}
    </div>
  );
}
